import { useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import Alert, { AlertColor, AlertProps } from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

import { loginUserMutation } from 'api/generated/@tanstack/react-query.gen';

import TextInput from 'components/FormInputs/TextInput';

import useAuthContext from 'hooks/useAuthContext';
import { useExternalMode } from 'hooks/useExternalMode';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

const formSchema = z.object({
  username: z.string(),
  password: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

function Form(): JSX.Element {
  const { accessToken, userRole, setAccessToken } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [alertData, setAlertData] = useState<{ message: string; severity: AlertProps['severity'] } | null>(null);
  const { isExternalMode } = useExternalMode();

  const login = useMutation({
    ...loginUserMutation(),
    onSuccess: (data) => {
      // update access token
      setAccessToken(data.accessToken);
    },
    onError: (error) => {
      // update the alert data with the error message
      setAlertData({
        message: getApiErrorMessage(error),
        severity: 'error',
      });
    },
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const { handleSubmit } = form;

  // set the form error message if the search params contain a message
  useEffect(() => {
    if (location.state?.message) {
      setAlertData({
        message: location.state.message,
        severity: (location.state.severity as AlertColor) ?? 'error',
      });
    }
  }, [location.state]);

  useEffect(() => {
    // check if the user is already logged in
    if (accessToken) {
      // if the user was redirected to the login page, redirect them back
      if (location.state?.from?.pathname && location.pathname !== location.state?.from?.pathname) {
        console.log('Returning back to original requested page');
        navigate(location.state?.from?.pathname, { replace: true });
        return;
      }

      // redirect the user based on their role
      if (userRole === 'ADMIN') {
        navigate('/admin');
      } else if (userRole === 'INSTRUCTOR') {
        navigate('/instructor');
      } else if (userRole === 'STUDENT') {
        navigate('/student');
      }
    }
  }, [accessToken, location, navigate, userRole]);

  const onSubmit = async ({ username, password }: FormValues) => {
    login.mutate({
      body: {
        username,
        password,
      },
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          <Box>
            <Typography
              sx={{
                textTransform: 'uppercase',
                fontWeight: 'medium',
              }}
              gutterBottom
              color={'text.secondary'}
            >
              Login
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
              }}
            >
              Welcome back
            </Typography>
            <Typography color="text.secondary">Login to manage your account.</Typography>
          </Box>

          <Collapse in={!!alertData && !login.isPending}>
            <Alert variant="outlined" severity={alertData?.severity}>
              {alertData?.message}
            </Alert>
          </Collapse>

          <TextInput name="username" label="Email" />
          <TextInput name="password" label="Password" type="password" />

          <Collapse in={login.isPending}>
            <LinearProgress />
          </Collapse>

          <Stack
            gap={2}
            flexDirection={{ xs: 'column-reverse', sm: 'row' }}
            alignItems={{ xs: 'stretched', sm: 'center' }}
            justifyContent={'space-between'}
          >
            <Button
              component={RouterLink}
              variant="text"
              to={isExternalMode ? '/external/forgot-password' : '/forgot-password'}
              sx={{ justifyContent: 'flex-start' }}
            >
              Forgot your password?
            </Button>
            <Button size="large" variant="contained" type="submit" disabled={login.isPending}>
              Login
            </Button>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
}

export default Form;
